import React, { useState, useEffect } from 'react';
import styles from '../styles/SessionDetailsModal.module.css';

/**
 * SessionDetailsModal - Shows session details with join/cancel/reschedule actions
 *
 * Props:
 * - session: Session object with coach, scheduled_start, etc.
 * - onClose: Callback to close modal
 * - onJoin: Callback when join button is clicked
 * - onReschedule: Callback when reschedule button is clicked
 * - onCancel: Callback when cancel button is clicked
 */
function SessionDetailsModal({ session, onClose, onJoin, onReschedule, onCancel, onBookNew }) {
  const [canJoin, setCanJoin] = useState(false);

  useEffect(() => {
    if (!session?.scheduled_start) return;

    const checkJoinWindow = () => {
      const now = new Date();
      const sessionStart = new Date(session.scheduled_start);
      const diffMin = Math.floor((sessionStart.getTime() - now.getTime()) / 60000);

      // Can join within ±10 minutes of start time
      setCanJoin(Math.abs(diffMin) <= 10);
    };

    checkJoinWindow();
    const interval = setInterval(checkJoinWindow, 60000);
    return () => clearInterval(interval);
  }, [session?.scheduled_start]);

  // If no session, show booking prompt
  if (!session) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h3 className={styles.title}>No Session Scheduled</h3>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>

          <div className={styles.content}>
            <p style={{ textAlign: 'center', color: '#666', marginBottom: '24px' }}>
              You don't have any upcoming coaching sessions. Book a session to get personalised guidance on your financial journey.
            </p>

            <div className={styles.actions}>
              <button className={styles.joinButton} onClick={onBookNew}>
                Book a Session
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    const day = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return { day, time };
  };

  const { day, time } = formatDateTime(session.scheduled_start);
  const coach = session.coach || {};

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>Coaching Session</h3>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.content}>
          {/* Coach Info */}
          <div className={styles.coachSection}>
            {coach.profileImage && (
              <img
                src={coach.profileImage}
                alt={coach.displayName || 'Coach'}
                className={styles.coachImage}
              />
            )}
            <div className={styles.coachInfo}>
              <div className={styles.coachName}>{coach.displayName || 'Your Coach'}</div>
              {coach.specializations && (
                <div className={styles.specializations}>{coach.specializations}</div>
              )}
            </div>
          </div>

          {/* Session Details */}
          <div className={styles.details}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Date</span>
              <span className={styles.detailValue}>{day}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Time</span>
              <span className={styles.detailValue}>{time}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Duration</span>
              <span className={styles.detailValue}>{coach.sessionDuration || 30} minutes</span>
            </div>
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            {canJoin ? (
              <button className={styles.joinButton} onClick={onJoin}>
                Join Session
              </button>
            ) : (
              <>
                {onReschedule && (
                  <button className={styles.rescheduleButton} onClick={onReschedule}>
                    Reschedule
                  </button>
                )}
                {onCancel && (
                  <button className={styles.cancelButton} onClick={onCancel}>
                    Cancel Session
                  </button>
                )}
              </>
            )}
          </div>

          {canJoin && (
            <div className={styles.joinNotice}>
              The join window is open! Click above to start your session.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SessionDetailsModal;
